import React, { type CSSProperties, type FC, useEffect, useState } from 'react'
import { Box, Card, CardActions, Collapse, IconButton, Paper, type SxProps, type Theme, Typography } from '@mui/material'
import { ExpandMore, WarningAmberOutlined } from '@mui/icons-material'
import { useSnackbar } from 'notistack'

import {
    Capability,
    createIsAuthenticatedWithCapabilitiesSelector,
    type CybusService,
    type CybusServiceDeviation,
    selectHasDeviations,
    selectServiceDeviationPage,
    Translation,
} from '../../../domain'

import { CircularLoader, Table, useTablePageChangeCallback } from '../common'
import { FormattedTranslation, useTranslator } from '../Internationalization'
import { AbsoluteRoutePathWithServiceId, usePermissionedLink } from '../routing'
import { useAppState, useAppUsecase } from '../State'

const DEVIATIONS_KEY = 'deviations'

const rootStyle: SxProps<Theme> = { color: 'inherit', background: 'inherit', maxWidth: 400, minWidth: 400 }
const actionsStyle: SxProps<Theme> = { p: 0, justifyContent: 'space-between' }
const linkStyle: SxProps<Theme> = { ml: 1, color: 'inherit' }
const expandStyle: SxProps<Theme> = {
    color: 'inherit',
    p: 2,
    transform: 'rotate(0deg)',
    transition: ({ transitions }) => transitions.create('transform', { duration: transitions.duration.shortest }),
}
const expandOpenStyle: SxProps<Theme> = { transform: 'rotate(180deg)' }
const deviationListStyle: SxProps<Theme> = { maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }
const notificationStyle: CSSProperties = { paddingLeft: '12px', paddingRight: '12px' }
const headerTitleIconStyle: SxProps<Theme> = { display: 'flex' }
const titleStyle: SxProps<Theme> = { fontWeight: 'bold', pl: 1 }
const nameStyle: SxProps<Theme> = { color: 'primary.main' }

const useServiceLink = (): FC<Readonly<{ id: CybusService['id'] }>> => {
    const PermissionedLink = usePermissionedLink()

    return ({ id }) => (
        <PermissionedLink sx={linkStyle} path={AbsoluteRoutePathWithServiceId.SERVICES_OVERVIEW_SERVICE} id={id}>
            <Typography data-testid="deviation-label" sx={nameStyle}>
                {id}
            </Typography>
        </PermissionedLink>
    )
}

const useDeviationTable = (): FC => {
    const ServiceLink = useServiceLink()
    const manageServicesDeviations = useAppUsecase('manageServicesDeviationsUsecase')

    return () => {
        const deviationsNotificationPage = useAppState(selectServiceDeviationPage)
        const onCustomized = useTablePageChangeCallback<CybusServiceDeviation>((s) => manageServicesDeviations.updateParameters(s), [manageServicesDeviations])

        if (deviationsNotificationPage === null) {
            return <CircularLoader data-testid="no-deviations-loader" />
        }

        return (
            <Table
                search={false}
                hideHeader
                columns={{ id: { label: Translation.SERVICE_ID, customCellRender: (id) => <ServiceLink id={id} /> } }}
                pagination={{
                    pageSize: deviationsNotificationPage.pageSize,
                    pageSizeOptions: [deviationsNotificationPage.pageSize],
                    hideJumpToPage: true,
                    page: deviationsNotificationPage.page,
                    totalCount: deviationsNotificationPage.totalCount,
                }}
                data={deviationsNotificationPage.current}
                onCustomized={onCustomized}
            />
        )
    }
}

const useDeviationMessages = (): FC => {
    const DeviationTable = useDeviationTable()

    return () => {
        const [expanded, setExpanded] = useState(false)
        const deviationsNotificationPage = useAppState(selectServiceDeviationPage)

        const handleExpanded: VoidFunction = () => {
            setExpanded(!expanded)
        }

        return (
            <Card sx={rootStyle} elevation={0}>
                <CardActions data-testid="deviation-card-actions" sx={[actionsStyle]}>
                    <Box sx={headerTitleIconStyle}>
                        <WarningAmberOutlined />
                        <Typography sx={titleStyle}>
                            <FormattedTranslation id={Translation.DEVIATION_DETECTED} values={{ count: deviationsNotificationPage?.totalCount }} />
                        </Typography>
                    </Box>

                    <Box>
                        <IconButton onClick={handleExpanded} sx={[expandStyle, expanded && expandOpenStyle]}>
                            <ExpandMore />
                        </IconButton>
                    </Box>
                </CardActions>
                <Collapse in={expanded} timeout="auto" unmountOnExit>
                    <Paper data-testid="deviations-list" sx={deviationListStyle}>
                        <DeviationTable />
                    </Paper>
                </Collapse>
            </Card>
        )
    }
}

const DeviationsTable: FC = () => {
    const { enqueueSnackbar, closeSnackbar } = useSnackbar()
    const DeviationMessages = useDeviationMessages()
    const showDeviations = useAppState(selectHasDeviations)
    const deviationsNotificationPage = useAppState(selectServiceDeviationPage)
    const subscribeUsecase = useAppUsecase('manageServicesDeviationsUsecase')

    useEffect(() => subscribeUsecase.subscribe({ short: true }), [subscribeUsecase])

    useEffect(() => {
        /**
         * This condition prevents closing the deviation notification during a page change
         * Reason: Pagination data (total count & current) temporarily becomes null during page changes
         * Note: Remove this condition when we fetch the deviation count separately
         */
        if (deviationsNotificationPage === null) {
            return
        }

        if (showDeviations) {
            enqueueSnackbar(<DeviationMessages />, {
                key: DEVIATIONS_KEY,
                variant: 'warning',
                persist: true,
                hideIconVariant: true,
                anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
                style: notificationStyle,
            })
        } else {
            closeSnackbar(DEVIATIONS_KEY)
        }
    }, [showDeviations, deviationsNotificationPage])

    useEffect(() => {
        return () => closeSnackbar(DEVIATIONS_KEY)
    }, [])

    return <></>
}

const ResolvedDeviations: FC = () => {
    const translator = useTranslator()
    const { enqueueSnackbar } = useSnackbar()
    const serviceDeviationResolution = useAppState((s) => s.serviceDeviationResolution)
    const subscribeUsecase = useAppUsecase('manageResolvedDeviationsUsecase')

    useEffect(() => subscribeUsecase.subscribe(), [subscribeUsecase])

    useEffect(() => {
        if (serviceDeviationResolution === null) {
            return
        }

        enqueueSnackbar(
            <div data-testid="resolved-deviation">{translator.formatTranslation(Translation.DEVIATION_RESOLVED, { id: serviceDeviationResolution.id })}</div>,
            { key: serviceDeviationResolution.id, variant: 'info' }
        )
    }, [serviceDeviationResolution])

    return <></>
}

const selectCanReadDeviations = createIsAuthenticatedWithCapabilitiesSelector(Capability.DEVIATIONS_READ)

export const Notifications: FC = () => {
    const canReadDeviations = useAppState(selectCanReadDeviations)

    return canReadDeviations ? (
        <>
            <DeviationsTable />
            <ResolvedDeviations />
        </>
    ) : null
}
